class PageTextTestimonials extends HTMLElement {
  constructor() {
    super();
    this.photosSwiper = null;
    this.contentSwiper = null;
    this.swiperInitAttempts = 0;
    this.maxSwiperInitAttempts = 40;
    this.swiperInitTimer = null;
    this.onResize = this.onResize.bind(this);
  }

  connectedCallback() {
    if (this.dataset.ready === 'true') {
      return;
    }

    this.dataset.ready = 'true';
    this.initSwipersWithRetry();
    this.bindReadMore();
    this.evaluateOverflow();
    window.addEventListener('resize', this.onResize, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);

    if (this.swiperInitTimer) {
      window.clearTimeout(this.swiperInitTimer);
      this.swiperInitTimer = null;
    }

    if (this.photosSwiper) {
      this.photosSwiper.destroy(true, true);
      this.photosSwiper = null;
    }

    if (this.contentSwiper) {
      this.contentSwiper.destroy(true, true);
      this.contentSwiper = null;
    }
  }

  initSwipersWithRetry() {
    if (this.initSwipers()) {
      return;
    }

    if (this.swiperInitAttempts >= this.maxSwiperInitAttempts) {
      return;
    }

    this.swiperInitAttempts += 1;
    this.swiperInitTimer = window.setTimeout(() => {
      this.initSwipersWithRetry();
    }, 120);
  }

  initSwipers() {
    if (typeof window.Swiper !== 'function') {
      return false;
    }

    const photosEl = this.querySelector('[data-testimonials-photos-swiper]');
    const contentEl = this.querySelector('[data-testimonials-content-swiper]');
    const prevEl = this.querySelector('[data-testimonials-prev]');
    const nextEl = this.querySelector('[data-testimonials-next]');

    if (!photosEl || !contentEl) {
      return false;
    }

    this.ensureMinimumSlidesForLoop(photosEl, contentEl, 10);

    const photoSlides = photosEl.querySelectorAll('.swiper-slide');
    const contentSlides = contentEl.querySelectorAll('.swiper-slide');
    const totalSlides = photoSlides.length;

    if (!totalSlides || totalSlides !== contentSlides.length) {
      return false;
    }

    const photosPerViewDesktop = Math.min(5, totalSlides);
    const photosPerViewTablet = Math.min(3, totalSlides);
    const canLoop = totalSlides >= 6;

    this.photosSwiper = new window.Swiper(photosEl, {
      slidesPerView: 1,
      centeredSlides: true,
      loop: canLoop,
      rewind: !canLoop,
      watchSlidesProgress: true,
      allowTouchMove: false,
      simulateTouch: false,
      speed: 420,
      breakpoints: {
        769: {
          slidesPerView: photosPerViewDesktop
        },
        476: {
          slidesPerView: photosPerViewTablet
        },
        0: {
          slidesPerView: 1
        },
      },
    });

    this.contentSwiper = new window.Swiper(contentEl, {
      slidesPerView: 1,
      loop: canLoop,
      rewind: !canLoop,
      speed: 420,
      allowTouchMove: false,
      simulateTouch: false,
    });

    if (prevEl) {
      prevEl.addEventListener('click', () => {
        if (this.photosSwiper) {
          this.photosSwiper.slidePrev();
        }
      });
    }

    if (nextEl) {
      nextEl.addEventListener('click', () => {
        if (this.photosSwiper) {
          this.photosSwiper.slideNext();
        }
      });
    }

    this.photosSwiper.on('slideChangeTransitionStart', () => {
      this.syncContentToPhotos();
      this.resetExpandedStates();
      this.evaluateOverflow();
    });

    this.contentSwiper.on('slideChangeTransitionStart', () => {
      this.syncPhotosToContent();
      this.resetExpandedStates();
      this.evaluateOverflow();
    });

    this.classList.add('is-swiper-ready');
    // Re-run: the connectedCallback pass may have seen an empty DOM
    // (parser upgrade), leaving the initial slide without its state.
    this.evaluateOverflow();
    return true;
  }

  ensureMinimumSlidesForLoop(photosEl, contentEl, minimumSlides) {
    const photosWrapper = photosEl.querySelector('.swiper-wrapper');
    const contentWrapper = contentEl.querySelector('.swiper-wrapper');

    if (!photosWrapper || !contentWrapper) {
      return;
    }

    photosWrapper.querySelectorAll('[data-generated-clone="true"]').forEach((slide) => slide.remove());
    contentWrapper.querySelectorAll('[data-generated-clone="true"]').forEach((slide) => slide.remove());

    const photoSlides = Array.from(photosWrapper.children);
    const contentSlides = Array.from(contentWrapper.children);

    if (!photoSlides.length || photoSlides.length !== contentSlides.length) {
      return;
    }

    if (photoSlides.length >= minimumSlides) {
      return;
    }

    const clonesNeeded = minimumSlides - photoSlides.length;

    for (let index = 0; index < clonesNeeded; index += 1) {
      const sourceIndex = index % photoSlides.length;

      const photoClone = photoSlides[sourceIndex].cloneNode(true);
      const contentClone = contentSlides[sourceIndex].cloneNode(true);

      photoClone.setAttribute('data-generated-clone', 'true');
      contentClone.setAttribute('data-generated-clone', 'true');

      photosWrapper.appendChild(photoClone);
      contentWrapper.appendChild(contentClone);
    }
  }

  syncContentToPhotos() {
    if (!this.photosSwiper || !this.contentSwiper) {
      return;
    }

    const logicalIndex = this.photosSwiper.realIndex;
    if (this.contentSwiper.realIndex === logicalIndex) {
      return;
    }

    if (this.contentSwiper.params.loop) {
      this.contentSwiper.slideToLoop(logicalIndex);
    } else {
      this.contentSwiper.slideTo(logicalIndex);
    }
  }

  syncPhotosToContent() {
    if (!this.photosSwiper || !this.contentSwiper) {
      return;
    }

    const logicalIndex = this.contentSwiper.realIndex;
    if (this.photosSwiper.realIndex === logicalIndex) {
      return;
    }

    if (this.photosSwiper.params.loop) {
      this.photosSwiper.slideToLoop(logicalIndex);
    } else {
      this.photosSwiper.slideTo(logicalIndex);
    }
  }

  goToLogicalIndex(index) {
    if (!this.photosSwiper || !this.contentSwiper) {
      return;
    }

    if (this.photosSwiper.params.loop) {
      this.photosSwiper.slideToLoop(index);
    } else {
      this.photosSwiper.slideTo(index);
    }

    if (this.contentSwiper.params.loop) {
      this.contentSwiper.slideToLoop(index);
    } else {
      this.contentSwiper.slideTo(index);
    }
  }

  bindReadMore() {
    // Delegated: buttons may not exist yet when connectedCallback runs
    // (parser upgrade), and loop clones are added after binding.
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-more-button]');
      if (!button) {
        return;
      }
      const card = button.closest('[data-content-inner]');
      if (card) {
        card.classList.add('is-expanded');
      }
    });
  }

  resetExpandedStates() {
    const cards = this.querySelectorAll('[data-content-inner]');
    cards.forEach((card) => {
      card.classList.remove('is-expanded');
    });
  }

  evaluateOverflow() {
    const mobile = window.matchMedia('(max-width: 768px)').matches;
    const cards = this.querySelectorAll('[data-content-inner]');
    cards.forEach((card) => {
      const wrapper = card.querySelector('[data-text-wrapper]');
      if (!wrapper) {
        return;
      }

      card.classList.remove('has-overflow');
      if (!mobile) {
        return;
      }

      if (wrapper.scrollHeight > wrapper.clientHeight + 2) {
        card.classList.add('has-overflow');
      }
    });
  }

  onResize() {
    this.evaluateOverflow();
  }
}

if (!customElements.get('page-text-testimonials')) {
  customElements.define('page-text-testimonials', PageTextTestimonials);
}
